import React, {useCallback, useEffect, useRef, useMemo, useState} from 'react';
import Col from 'react-bootstrap/Col';
import {Row} from 'react-bootstrap';
import {BarOrientation, ChartOutput, ChartQuery, ChartSettings} from 'entities/chart';
import {FormikT} from 'utils/types';
import {
    ChartType,
    EventFilterGroup,
} from '@/views/storyboard/utils/chartConstants';
import {applyChartTypeChange, cleanupChartSettingsForTypeAndOutput} from '@/views/storyboard/utils/applyChartTypeChange';
import {
    buildChartSettingsFromParts,
    ChartTypeSettingsCache,
    extractSharedChartQuery,
    extractSharedChartSettings,
    restoreChartTypeState,
} from '@/views/storyboard/utils/chartTypeSettingsCache';
import useData from 'utils/useData';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import CellLoader from '@/components/loading/CellLoader';
import ChartSettingsQueryPanel, {ChartSettingsQueryPanelRef} from '@/views/storyboard/chart_settings/ChartSettingsQueryPanel';
import ChartSettingsVisualizationPanel from '@/views/storyboard/chart_settings/ChartSettingsVisualizationPanel';
import ChartSettingsPreviewColumn from '@/views/storyboard/chart_settings/ChartSettingsPreviewColumn';
import {flattenEventFilters} from '@/views/storyboard/chart_settings/flattenEventFilters';
import {CustomizeFormField} from 'entities/chartFilters';
import {useRenderDiagnostics} from '@/hooks/useRenderDiagnostics';
import {DateTime} from 'luxon';
import {ChartFooterData} from '@/views/storyboard/charts/utils/chartFooterData';

const cartesianTimelineChartTypes = [
    ChartType.BarChart,
    ChartType.LineChart,
    ChartType.AreaChart,
];

const aggregationChartTypes = [
    ChartType.PieChart,
    ChartType.SunburstChart,
    ChartType.TreemapChart,
    ChartType.Table,
];

const formatPreviewDatePeriod = (
    startDate?: string,
    endDate?: string,
    fallbackLabel?: string,
) => {
    if (!startDate || !endDate) {
        return fallbackLabel || '';
    }

    const start = DateTime.fromFormat(startDate, 'dd/MM/yyyy');
    const end = DateTime.fromFormat(endDate, 'dd/MM/yyyy');

    if (!start.isValid || !end.isValid) {
        return fallbackLabel || `${startDate}-${endDate}`;
    }

    if (start.year === end.year) {
        return `${start.toFormat('MMM d')} – ${end.toFormat('MMM d, yyyy')}`;
    }

    return `${start.toFormat('MMM d, yyyy')} – ${end.toFormat('MMM d, yyyy')}`;
};

interface Props {
    chartQueryFormik: FormikT<ChartQuery>;
    chartSettingsFormik: FormikT<ChartSettings>;
    chartTitleInputRef?: React.RefObject<import('./ChartSettingsTitleEditor').ChartSettingsTitleEditorRef>;
    chartDescriptionInputRef?: React.RefObject<import('./ChartDescriptionInput').ChartDescriptionInputRef>;
    chartQueryPanelRef?: React.RefObject<ChartSettingsQueryPanelRef>;
    showTitleRequiredError?: boolean;
}

type WordCloudFilteringList = [number, string];

const ChartSettingsForm = React.memo<Props>((
    {
        chartQueryFormik,
        chartSettingsFormik,
        chartTitleInputRef,
        showTitleRequiredError = false,
        chartQueryPanelRef,
    },
) => {
    const [
        rawCustomizeFormFieldChoices,
        rawLoadingCustomizeFormFieldChoices,
    ] = useData<CustomizeFormField[]>(
        window.URLS.customizeFormFieldChoices,
        'customizeFormFieldChoices',
        'loadCustomizeFormFieldChoices',
    );

    const customizeFormFieldChoices = useMemo(() => rawCustomizeFormFieldChoices, [rawCustomizeFormFieldChoices?.length]);
    const loadingCustomizeFormFieldChoices = useMemo(() => rawLoadingCustomizeFormFieldChoices, [rawLoadingCustomizeFormFieldChoices]);

    const [
        rawWordcloudFilteringListsChoices,
        rawLoadingWordcloudFilteringListsChoices,
    ] = useData<WordCloudFilteringList[]>(window.URLS.wordcloudFilteringListsChoices, {useLocalStore: true});

    const wordcloudFilteringListsChoices = useMemo(() => rawWordcloudFilteringListsChoices, [rawWordcloudFilteringListsChoices?.length]);
    const loadingWordcloudFilteringListsChoices = useMemo(() => rawLoadingWordcloudFilteringListsChoices, [rawLoadingWordcloudFilteringListsChoices]);

    const [rawEventFilterGroupList, rawLoadingEventFilters] = useData<EventFilterGroup>(window.URLS.eventFiltersResource, {
        storeResolver: 'eventFilters',
        actionResolver: 'loadEventFilters',
        useCachedData: true,
    });

    const eventFilterGroupList = useMemo(() => rawEventFilterGroupList, [rawEventFilterGroupList?.core?.length, rawEventFilterGroupList?.prioritised?.length]);
    const loadingEventFilters = useMemo(() => rawLoadingEventFilters, [rawLoadingEventFilters]);
    const eventFilters = useMemo(() => flattenEventFilters(eventFilterGroupList), [eventFilterGroupList]);

    const rawOptimizedConfig = useOptimizedConfig();
    const userData = useMemo(() => rawOptimizedConfig.userData, [
        rawOptimizedConfig.userData?.id,
        rawOptimizedConfig.userData?.restrictedFieldKeys,
        rawOptimizedConfig.userData?.sessionDateRangeLabel,
        rawOptimizedConfig.userData?.sessionStartDate,
        rawOptimizedConfig.userData?.sessionEndDate,
    ]);
    const loadingUserData = useMemo(() => rawOptimizedConfig.loading, [rawOptimizedConfig.loading]);

    const chartTypeSettingsCacheRef = useRef(new ChartTypeSettingsCache());
    const [previewFooterData, setPreviewFooterData] = useState<ChartFooterData>({});

    useRenderDiagnostics('ChartSettingsForm', {
        chartType: chartSettingsFormik.values.type,
        output: chartQueryFormik.values.output,
        queryString: chartQueryFormik.values.query_string,
        aggsItem: chartQueryFormik.values.aggs_item,
    });

    useEffect(() => {
        chartTypeSettingsCacheRef.current.seed(
            chartSettingsFormik.values.type as ChartType,
            chartQueryFormik.values,
            chartSettingsFormik.values,
        );
    }, []);

    const handleChartTypeChange = useCallback((nextType: ChartType, nextBarOrientation?: BarOrientation) => {
        const currentType = chartSettingsFormik.values.type as ChartType;
        if (currentType === nextType) {
            if (
                nextType === ChartType.BarChart
                && nextBarOrientation
                && chartSettingsFormik.values.barOrientation !== nextBarOrientation
            ) {
                chartSettingsFormik.setFieldValue('barOrientation', nextBarOrientation, false);
            }
            return;
        }

        const cache = chartTypeSettingsCacheRef.current;
        cache.save(currentType, chartQueryFormik.values, chartSettingsFormik.values);

        const sharedQuery = extractSharedChartQuery(chartQueryFormik.values);
        const sharedSettings = extractSharedChartSettings(chartSettingsFormik.values);
        const preserveCurrentQueryShape = cartesianTimelineChartTypes.includes(currentType)
            && cartesianTimelineChartTypes.includes(nextType);
        const preserveCurrentAggregationShape = aggregationChartTypes.includes(currentType)
            && aggregationChartTypes.includes(nextType)
            && (
                chartQueryFormik.values.output === ChartOutput.Terms
                || chartQueryFormik.values.output === ChartOutput.MultiTerms
                || chartQueryFormik.values.output === ChartOutput.NestedTerms
            );

        let chartQuery: ChartQuery;
        let chartSettings: ChartSettings;

        const cachedState = cache.get(nextType);
        if (cachedState && !preserveCurrentQueryShape && !preserveCurrentAggregationShape) {
            ({chartQuery, chartSettings} = restoreChartTypeState(
                nextType,
                sharedQuery,
                sharedSettings,
                cachedState,
                eventFilters,
            ));
        } else {
            ({chartQuery, chartSettings} = applyChartTypeChange(
                nextType,
                chartQueryFormik.values,
                chartSettingsFormik.values,
                eventFilters,
            ));
            if (cachedState) {
                chartSettings = cleanupChartSettingsForTypeAndOutput(
                    buildChartSettingsFromParts(
                        sharedSettings,
                        cachedState.settings,
                        nextType,
                    ),
                    nextType,
                    chartQuery.output,
                );
            }
            cache.save(nextType, chartQuery, chartSettings);
        }

        if (nextType === ChartType.BarChart && nextBarOrientation) {
            chartSettings.barOrientation = nextBarOrientation;
        }

        chartQueryFormik.setValues(chartQuery, false);
        chartSettingsFormik.setValues(chartSettings, false);
    }, [chartQueryFormik, chartSettingsFormik, eventFilters]);

    const updatePreviewFooterData = useCallback((footerData: ChartFooterData) => {
        setPreviewFooterData((current) => {
            const next = {
                ...current,
                ...footerData,
            };

            return Object.keys(next).every((key) => (
                current[key as keyof ChartFooterData] === next[key as keyof ChartFooterData]
            ))
                ? current
                : next;
        });
    }, []);

    const previewDatePeriod = formatPreviewDatePeriod(
        userData?.sessionStartDate,
        userData?.sessionEndDate,
        userData?.sessionDateRangeLabel,
    );

    useEffect(() => {
        setPreviewFooterData({});
    }, [
        chartSettingsFormik.values.type,
        chartQueryFormik.values.query_string,
        chartQueryFormik.values.output,
    ]);

    if (loadingWordcloudFilteringListsChoices || loadingCustomizeFormFieldChoices || loadingUserData) {
        return (
            <div className="chart-settings-form-loader">
                <CellLoader fullHeight={false} />
            </div>
        );
    }

    return (
        <div className="form-container chart-settings-form">
            <Row className="chart-settings-form-row">
                <Col md={3} className="chart-settings-form-col">
                    <ChartSettingsQueryPanel
                        ref={chartQueryPanelRef}
                        chartQueryFormik={chartQueryFormik}
                        customizeFormFieldChoices={customizeFormFieldChoices}
                    />
                </Col>

                <Col md={3} className="chart-settings-form-col">
                    <ChartSettingsVisualizationPanel
                        chartQueryFormik={chartQueryFormik}
                        chartSettingsFormik={chartSettingsFormik}
                        eventFilters={eventFilters}
                        eventFiltersLoading={loadingEventFilters}
                        userData={userData}
                        wordcloudFilteringListsChoices={wordcloudFilteringListsChoices}
                        onChartTypeChange={handleChartTypeChange}
                    />
                </Col>

                <Col md={6} className="chart-settings-form-col chart-settings-form-col--output">
                    <ChartSettingsPreviewColumn
                        chartQueryFormik={chartQueryFormik}
                        chartSettingsFormik={chartSettingsFormik}
                        chartTitleInputRef={chartTitleInputRef}
                        showTitleRequiredError={showTitleRequiredError}
                        previewDatePeriod={previewDatePeriod}
                        previewFooterData={previewFooterData}
                        onFooterDataChange={updatePreviewFooterData}
                    />
                </Col>
            </Row>
        </div>
    );
});

export default ChartSettingsForm;
